import type { AxiosRequestConfig } from 'axios'
import { api } from '@/lib/api'
import type { InviteResponseChoice } from '@repo/types'

// ─── Types ────────────────────────────────────────────────────────────────
// Mirror of the API DTOs/Prisma rows the admin webmail consumes. The shared
// axios interceptor already unwraps the single-resource `{ data }` envelope,
// and keeps paginated `{ data, meta }` intact.

export type EmailAccountStatus = 'OK' | 'AUTH_ERROR' | 'CONN_ERROR' | 'DISABLED'
export type EmailFolderRole = 'INBOX' | 'SENT' | 'DRAFTS' | 'TRASH' | 'SPAM' | 'ARCHIVE' | 'OTHER'
export type EmailDirection = 'INBOUND' | 'OUTBOUND'
export type EmailMessageStatus =
  | 'RECEIVED' | 'DRAFT' | 'SCHEDULED' | 'QUEUED' | 'SENDING' | 'SENT' | 'FAILED'

/** Account as returned by the API — never includes `passwordEnc`. */
export interface EmailAccount {
  id: string
  tenantId: string
  userId: string
  label: string
  emailAddress: string
  imapHost: string
  imapPort: number
  imapSecure: boolean
  smtpHost: string
  smtpPort: number
  smtpSecure: boolean
  username: string
  signatureHtml: string | null
  syncEnabled: boolean
  lastSyncedAt: string | null
  status: EmailAccountStatus
  errorMsg: string | null
  createdAt: string
  updatedAt: string
}

export type EmailBulkAction = 'read' | 'unread' | 'trash' | 'move'

export interface EmailFolder {
  id: string
  accountId: string
  path: string
  name: string
  role: EmailFolderRole
  /** unread count, added by the folders endpoint */
  unread: number
}

/** Lightweight attachment info in the message list (non-inline only). */
export interface EmailAttachmentSummary {
  filename: string
  mimeType: string
}

/** Lightweight row for the message-list pane. */
export interface EmailMessageListItem {
  id: string
  /** Priečinok správy (rola cez `folders`) — Archív/Spam/Kôš otáčajú akcie na návrat do Prijatých. */
  folderId: string | null
  subject: string | null
  snippet: string | null
  fromName: string | null
  fromAddress: string | null
  toAddresses: string[]
  /** Pre CC indikátor (emailRecipientScope z @repo/types). */
  ccAddresses?: string[]
  sentAt: string | null
  isRead: boolean
  isFlagged: boolean
  /** IMAP \Answered / $Forwarded — indikátory odpovedané/preposlané. */
  isAnswered?: boolean
  isForwarded?: boolean
  hasAttachments: boolean
  attachments: EmailAttachmentSummary[]
  direction: EmailDirection
  contactId: string | null
}

export interface EmailAttachment {
  id: string
  messageId: string | null
  filename: string
  mimeType: string
  sizeBytes: number
  contentId: string | null
  isInline: boolean
}

export type InviteResponseStatus = 'NEEDS_ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE'
export type { InviteResponseChoice }

/** ICS calendar invite carried by an email (REQUEST/CANCEL) — null when the message has none. */
export interface EmailInvite {
  id: string
  method: 'REQUEST' | 'CANCEL'
  uid: string
  sequence: number
  summary: string
  location: string | null
  description: string | null
  dtStart: string
  dtEnd: string | null
  allDay: boolean
  organizerEmail: string | null
  organizerName: string | null
  isRecurring: boolean
  responseStatus: InviteResponseStatus
  respondedAt: string | null
  eventId: string | null
  cancelled: boolean
  superseded: { dtStart: string; dtEnd: string | null; cancelled: boolean } | null
}

/** Full message for the reader pane (includes body + attachments). */
export interface EmailMessageDetail extends EmailMessageListItem {
  ccAddresses: string[]
  bccAddresses: string[]
  bodyHtml: string | null
  bodyText: string | null
  receivedAt: string | null
  status: EmailMessageStatus
  scheduledAt: string | null
  inReplyTo: string | null
  threadId: string | null
  accountId: string
  dealId: number | null
  createdAt: string
  attachments: EmailAttachment[]
  invite: EmailInvite | null
  /**
   * Presigned URL inline častí, na ktoré telo odkazovalo cez `cid:` (server ich
   * v `bodyHtml` už nahradil). Sú súčasťou správy, nie vzdialený obsah — gate na
   * blokovanie obrázkov ich preskakuje.
   */
  inlineImageUrls: string[]
}

export interface Paginated<T> {
  data: T[]
  meta: { page: number; take: number; total: number }
}

export interface CreateEmailAccountInput {
  label: string
  emailAddress: string
  imapHost: string
  imapPort: number
  imapSecure?: boolean
  smtpHost: string
  smtpPort: number
  smtpSecure?: boolean
  username: string
  password: string
}

export type UpdateEmailAccountInput = Partial<CreateEmailAccountInput>

export interface DiscoveredMailboxConfig {
  imapHost: string
  imapPort: number
  imapSecure: boolean
  smtpHost: string
  smtpPort: number
  smtpSecure: boolean
  username: string
  source: 'ispdb' | 'srv' | 'mx'
}

export interface AutodiscoverResult {
  found: boolean
  config?: DiscoveredMailboxConfig
}

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward'

export interface ComposeInput {
  accountId: string
  mode: ComposeMode
  inReplyToId?: string
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  bodyHtml: string
  attachmentIds?: string[]
  scheduledAt?: string
}

export interface TestResult {
  ok: boolean
  error?: string
}

export type RecipientType = 'contact' | 'deal' | 'user' | 'history'

/** A composer recipient-autocomplete suggestion from GET /email/recipients. */
export interface RecipientSuggestion {
  type: RecipientType
  email: string
  name: string
  sublabel?: string
  /** Present for type=user — the colleague's UserRole. */
  role?: string
}

export interface CommHistoryItem {
  type: 'email'
  id: string
  direction: string
  date: string | null
  subject: string | null
  brokerName: string
  canOpen: boolean
  messageId: string | null
}

// ─── Accounts ───────────────────────────────────────────────────────────────

export const emailApi = {
  accounts: {
    async list(): Promise<EmailAccount[]> {
      const { data } = await api.get('/email/accounts')
      return data
    },
    /** Chybu overenia IMAP/SMTP ukazuje formulár inline. */
    async create(input: CreateEmailAccountInput): Promise<EmailAccount> {
      const { data } = await api.post('/email/accounts', input, { skipErrorToast: true })
      return data
    },
    async autodiscover(emailAddress: string): Promise<AutodiscoverResult> {
      const { data } = await api.post('/email/accounts/autodiscover', { emailAddress })
      return data
    },
    async update(id: string, input: UpdateEmailAccountInput): Promise<EmailAccount> {
      const { data } = await api.patch(`/email/accounts/${id}`, input)
      return data
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/email/accounts/${id}`)
    },
    /** Výsledok aj chybu testu ukazuje formulár inline. */
    async test(id: string): Promise<TestResult> {
      const { data } = await api.post(`/email/accounts/${id}/test`, undefined, { skipErrorToast: true })
      return data
    },
    async sync(id: string): Promise<{ queued: boolean }> {
      const { data } = await api.post(`/email/accounts/${id}/sync`)
      return data
    },
    async updateSignature(id: string, signatureHtml: string): Promise<EmailAccount> {
      const { data } = await api.patch(`/email/accounts/${id}/signature`, { signatureHtml })
      return data
    },
  },

  // ─── Folders ────────────────────────────────────────────────────────────
  async folders(accountId: string): Promise<EmailFolder[]> {
    const { data } = await api.get('/email/folders', { params: { accountId } })
    return data
  },

  // ─── Messages ───────────────────────────────────────────────────────────
  messages: {
    async list(params: {
      accountId: string
      folder?: string
      q?: string
      page?: number
      /** Len neprečítané (prepínač v zozname). */
      unread?: boolean
      /** Len s hviezdičkou — virtuálny pohľad naprieč priečinkami účtu (bez `folder`). */
      flagged?: boolean
    }): Promise<Paginated<EmailMessageListItem>> {
      const { data } = await api.get('/email/messages', { params })
      return data
    },
    async get(id: string, config?: AxiosRequestConfig): Promise<EmailMessageDetail> {
      const { data } = await api.get(`/email/messages/${id}`, config)
      return data
    },
    async markRead(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/read`)
      return data
    },
    async markUnread(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/unread`)
      return data
    },
    async flag(id: string, isFlagged: boolean): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/flag`, { isFlagged })
      return data
    },
    async trash(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/trash`)
      return data
    },
    async spam(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/spam`)
      return data
    },
    async archive(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/archive`)
      return data
    },
    /**
     * Accept / tentatively-accept / decline an ICS invite carried by a message.
     * Returns the FULL fresh message detail (identical shape to GET), so the
     * caller can write it straight into the detail query slot. 409 = banner stale
     * (invalidate + refetch), 400 = organizer already cancelled.
     */
    async respondInvite(id: string, response: InviteResponseChoice): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/messages/${id}/invite/respond`, { response })
      return data
    },
    /**
     * Bulk mark-read / mark-unread / trash for the list multi-select; `move`
     * presunie správy do priečinka `folderId` tej istej schránky (Archív, Spam,
     * návrat do Doručených).
     */
    async bulk(ids: string[], action: EmailBulkAction, folderId?: string): Promise<{ count: number }> {
      const { data } = await api.post('/email/messages/bulk', { ids, action, ...(folderId ? { folderId } : {}) })
      return data
    },
  },

  /** Unread INBOX counts — total (sidebar badge) + per-account (switcher badges). */
  async unreadCounts(): Promise<{ total: number; accounts: { accountId: string; unread: number }[] }> {
    const { data } = await api.get('/email/messages/unread-counts')
    return data
  },

  // ─── Compose / drafts / schedule ──────────────────────────────────────────
  async send(input: ComposeInput): Promise<EmailMessageDetail> {
    const { data } = await api.post('/email/send', input)
    return data
  },
  drafts: {
    async create(input: ComposeInput): Promise<EmailMessageDetail> {
      const { data } = await api.post('/email/drafts', input)
      return data
    },
    async update(id: string, input: ComposeInput): Promise<EmailMessageDetail> {
      const { data } = await api.patch(`/email/drafts/${id}`, input)
      return data
    },
    async send(id: string): Promise<EmailMessageDetail> {
      const { data } = await api.post(`/email/drafts/${id}/send`)
      return data
    },
  },
  async cancelSchedule(id: string): Promise<EmailMessageDetail> {
    const { data } = await api.delete(`/email/schedule/${id}`)
    return data
  },

  // ─── Attachments ──────────────────────────────────────────────────────────
  /**
   * Skopíruje ne-inline prílohy správy ako staged prílohy pre preposlanie
   * (POST /email/messages/:id/attachments/stage). Compose berie len staged
   * prílohy (messageId=null) — server preto vyrába kópie, originálu nič nezmizne.
   */
  async stageForwardAttachments(messageId: string): Promise<EmailAttachment[]> {
    // Composer pri chybe vysvetlí, že sa prepošle bez príloh.
    const { data } = await api.post(`/email/messages/${messageId}/attachments/stage`, undefined, { skipErrorToast: true })
    return data
  },
  async uploadAttachment(file: File): Promise<EmailAttachment> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post('/email/attachments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },
  /**
   * Resolves a presigned, absolute download URL for a message attachment.
   * Hits GET /email/messages/:id/attachments/:aid which returns `{ url }`.
   * (Previously a relative-path helper existed here that was unusable as a
   * direct href because it omitted the API base — replaced by this wrapper.)
   */
  async attachmentUrl(messageId: string, attachmentId: string): Promise<string> {
    // Chybu otvorenia ukazuje volajúci (aj pri výpadku spojenia, ktorý interceptor pri GET netoastuje).
    const { data } = await api.get(`/email/messages/${messageId}/attachments/${attachmentId}`, { skipErrorToast: true })
    return (data as { url: string }).url
  },

  // ─── Recipient search (composer autocomplete) ─────────────────────────────
  async searchRecipients(q: string): Promise<RecipientSuggestion[]> {
    const { data } = await api.get('/email/recipients', { params: { q } })
    return data
  },

  // ─── Comm history ─────────────────────────────────────────────────────────
  async commHistory(params: {
    contactId?: string
    dealId?: number
  }): Promise<CommHistoryItem[]> {
    const { data } = await api.get('/email/comm-history', { params })
    return data
  },
}
